// src/components/tiptap/extensions/RandomisePracticeDiceNode.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../../../common/SafeIcon.jsx";
import ConfirmModal from "../../common/ConfirmModal.jsx";

const { FiShuffle, FiPlay, FiTrash2, FiRefreshCcw, FiPlus, FiMinus } = FiIcons;

const SOUND_URL =
  "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/tools/images/dice/rolling-sound.mp3";

const DICE_IMAGE_BASE =
  "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/tools/images/dice";

const cls = (...xs) => xs.filter(Boolean).join(" ");

/* helper to allow form controls to keep default behavior */
const isFormControl = (el) =>
  el && (el.tagName === "SELECT" || el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);

function diceImgUrl(n) {
  const v = Math.max(1, Math.min(6, Number(n) || 1));
  return `${DICE_IMAGE_BASE}/${v}.png`;
}

function rand1to6() {
  return 1 + Math.floor(Math.random() * 6);
}

const ASPECTS = [
  { key: "self", label: "I’ll decide myself" },
  { key: "reps", label: "Number of repetitions" },
  { key: "metronome", label: "Metronome speed" },
  { key: "barBegin", label: "Bar number to begin at" },
  { key: "barsCount", label: "How many bars to play" },
  { key: "hand", label: "Which hand" },
  { key: "dynamic", label: "Dynamic level" },
  { key: "tempoDynamic", label: "Combination: tempo and dynamic" },
];

const RHYTHM_VALUES = ["Semiquaver", "Quaver", "Crotchet", "Minim", "Dotted minim", "Semibreve"];
const METRONOME_MARKS = [50, 72, 94, 116, 138, 160]; // evenly spread 50..160

const DYNAMICS = ["pp", "p", "mp", "mf", "f", "ff"];
const TEMPO_BUCKETS = ["super slow", "slow", "moderate", "fast", "very fast", "super fast"];

function isTwoDiceRequired(aspectKey) {
  return aspectKey === "metronome" || aspectKey === "tempoDynamic";
}

function twoDigitFromDice(a, b) {
  const x = Math.max(1, Math.min(6, Number(a) || 1));
  const y = Math.max(1, Math.min(6, Number(b) || 1));
  return x * 10 + y; // e.g. 1 and 6 => 16; 6 and 2 => 62
}

function dynamicFromOneDie(d) {
  const idx = Math.max(1, Math.min(6, Number(d) || 1)) - 1;
  return DYNAMICS[idx];
}

function dynamicFromTwoDiceSum(sum2to12) {
  const s = Math.max(2, Math.min(12, Number(sum2to12) || 2));
  // Spread evenly across 2..12 (11 values) into 6 dynamics
  // 2-3 pp, 4-5 p, 6-7 mp, 8-9 mf, 10-11 f, 12 ff
  if (s <= 3) return "pp";
  if (s <= 5) return "p";
  if (s <= 7) return "mp";
  if (s <= 9) return "mf";
  if (s <= 11) return "f";
  return "ff";
}

function handFromDie(d, useSum = false) {
  const v = Number(d) || 1;
  if (!useSum) {
    const x = Math.max(1, Math.min(6, v));
    if (x <= 2) return "left";
    if (x <= 4) return "right";
    return "together";
  }
  const s = Math.max(2, Math.min(12, v));
  // 2-4 left, 5-8 right, 9-12 together
  if (s <= 4) return "left";
  if (s <= 8) return "right";
  return "together";
}

function buildInstruction({ aspect, diceCount, a, b }) {
  const die1 = Math.max(1, Math.min(6, Number(a) || 1));
  const die2 = Math.max(1, Math.min(6, Number(b) || 1));
  const sum = die1 + die2;

  switch (aspect) {
    case "reps": {
      const reps = diceCount === 2 ? sum : die1;
      return `Repeat it ${reps} time${reps === 1 ? "" : "s"}.`;
    }
    case "metronome": {
      // Requires 2 dice
      const rhythm = RHYTHM_VALUES[die1 - 1];
      const bpm = METRONOME_MARKS[die2 - 1];
      return `Play with the metronome on ${rhythm.toLowerCase()} = ${bpm}.`;
    }
    case "barBegin": {
      const bar = diceCount === 2 ? twoDigitFromDice(die1, die2) : die1;
      return `Begin at bar ${bar}.`;
    }
    case "barsCount": {
      const bars = diceCount === 2 ? twoDigitFromDice(die1, die2) : die1;
      return `Play ${bars} bar${bars === 1 ? "" : "s"}.`;
    }
    case "hand": {
      const which = diceCount === 2 ? handFromDie(sum, true) : handFromDie(die1, false);
      return `Play ${which} hand${which === "together" ? "s" : ""}.`;
    }
    case "dynamic": {
      const dyn = diceCount === 2 ? dynamicFromTwoDiceSum(sum) : dynamicFromOneDie(die1);
      return `Play at ${dyn}.`;
    }
    case "tempoDynamic": {
      // Requires 2 dice
      const tempo = TEMPO_BUCKETS[die1 - 1];
      const dyn = DYNAMICS[die2 - 1];
      return `Play at a ${tempo} tempo with ${dyn} dynamics.`;
    }
    case "self":
    default: {
      return `Your roll is ${diceCount === 2 ? `${die1} and ${die2}` : `${die1}`}. You decide what to change.`;
    }
  }
}

function DiceFace({ value, rolling, index = 0 }) {
  const src = diceImgUrl(value);
  return (
    <div
      className={cls(
        "dice relative w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center",
        rolling && "is-rolling"
      )}
      style={{
        // stagger a tiny bit so 2 dice don't look perfectly synchronized
        animationDelay: rolling ? `${index * 70}ms` : undefined,
      }}
      aria-label={`Die shows ${value}`}
    >
      <img
        src={src}
        alt={`Die ${Math.max(1, Math.min(6, Number(value) || 1))}`}
        draggable={false}
        className={cls(
          "w-20 h-20 sm:w-[92px] sm:h-[92px] object-contain select-none pointer-events-none",
          rolling ? "opacity-95" : "opacity-100"
        )}
        onError={(e) => {
          // Safety: if an image is missing, hide it rather than showing a broken icon.
          // (We still keep the card shell + animation.)
          try {
            e.currentTarget.style.opacity = "0";
          } catch {}
        }}
      />

      {/* subtle gloss */}
      <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-white/70 via-transparent to-slate-100/30" />
    </div>
  );
}

function RandomisePracticeDiceBlock({ node, updateAttributes, editor, deleteNode }) {
  const attrs = node.attrs || {};
  const ctx = editor?.storage?.mms ?? {};
  const canDelete = !!ctx?.userId && !!editor?.isEditable; // teachers only

  // Seed noteBlockId once
  useEffect(() => {
    if (!attrs.noteBlockId) {
      updateAttributes({ noteBlockId: crypto.randomUUID?.() ?? String(Date.now()) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [diceCount, setDiceCount] = useState(1); // 1 | 2 (not persisted)
  const [rolling, setRolling] = useState(false);
  const [result, setResult] = useState([1, 1]); // store both; render first N
  const [message, setMessage] = useState("");
  const [aspect, setAspect] = useState("self"); // defaults to "I'll decide myself" (not persisted)

  const audioRef = useRef(null); // HTMLAudioElement | null
  const soundOkRef = useRef(false);
  const rollIntervalRef = useRef(null);
  const endTimeoutRef = useRef(null);
  const endedHandlerRef = useRef(null);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const requiresTwoDice = useMemo(() => isTwoDiceRequired(aspect), [aspect]);

  // If the selected aspect requires 2 dice, force it (and keep UI consistent)
  useEffect(() => {
    if (requiresTwoDice && diceCount !== 2) setDiceCount(2);
  }, [requiresTwoDice, diceCount]);

  // Best-effort: preload dice images so the roll animation swaps smoothly
  useEffect(() => {
    try {
      const imgs = [];
      for (let i = 1; i <= 6; i++) {
        const im = new Image();
        im.src = diceImgUrl(i);
        imgs.push(im);
      }
    } catch {
      // ignore
    }
  }, []);

  // Prepare audio (best-effort). If it fails (404 etc), keep silent.
  useEffect(() => {
    let a = null;
    try {
      a = new Audio(SOUND_URL);
      a.preload = "auto";
      soundOkRef.current = false;

      const onCanPlay = () => {
        soundOkRef.current = true;
      };
      const onError = () => {
        soundOkRef.current = false;
      };

      a.addEventListener("canplaythrough", onCanPlay);
      a.addEventListener("canplay", onCanPlay);
      a.addEventListener("error", onError);

      audioRef.current = a;

      return () => {
        try {
          a.pause();
          a.currentTime = 0;
        } catch {}
        a.removeEventListener("canplaythrough", onCanPlay);
        a.removeEventListener("canplay", onCanPlay);
        a.removeEventListener("error", onError);
        audioRef.current = null;
      };
    } catch {
      audioRef.current = null;
      soundOkRef.current = false;
      return undefined;
    }
  }, []);

  // Cleanup timers/intervals on unmount
  useEffect(() => {
    return () => {
      try {
        if (rollIntervalRef.current) clearInterval(rollIntervalRef.current);
        if (endTimeoutRef.current) clearTimeout(endTimeoutRef.current);
      } catch {}
      rollIntervalRef.current = null;
      endTimeoutRef.current = null;

      try {
        const a = audioRef.current;
        if (a && endedHandlerRef.current) a.removeEventListener("ended", endedHandlerRef.current);
      } catch {}
      endedHandlerRef.current = null;
    };
  }, []);

  const stopRolling = (final = null) => {
    if (rollIntervalRef.current) clearInterval(rollIntervalRef.current);
    rollIntervalRef.current = null;

    if (endTimeoutRef.current) clearTimeout(endTimeoutRef.current);
    endTimeoutRef.current = null;

    // Remove ended handler if attached
    try {
      const a = audioRef.current;
      if (a && endedHandlerRef.current) a.removeEventListener("ended", endedHandlerRef.current);
    } catch {}
    endedHandlerRef.current = null;

    setRolling(false);

    const a = final?.[0] ?? rand1to6();
    const b = final?.[1] ?? rand1to6();
    const next = [a, b];
    setResult(next);

    const instruction = buildInstruction({
      aspect,
      diceCount: (requiresTwoDice ? 2 : diceCount),
      a,
      b,
    });
    setMessage(instruction);
  };

  const startRolling = async () => {
    if (rolling) return;

    setMessage("");
    setRolling(true);

    // Start a “shuffle” of faces while rolling
    rollIntervalRef.current = setInterval(() => {
      setResult([rand1to6(), rand1to6()]);
    }, 90);

    const a = audioRef.current;
    const hasSound = !!a && soundOkRef.current;

    if (hasSound) {
      // Stop when the sound ends (animation must last as long as sound)
      try {
        a.pause();
        a.currentTime = 0;

        const onEnded = () => {
          stopRolling([rand1to6(), rand1to6()]);
        };
        endedHandlerRef.current = onEnded;
        a.addEventListener("ended", onEnded, { once: true });

        const p = a.play();
        if (p && typeof p.catch === "function") {
          p.catch(() => {
            // If play is blocked (rare after a click), fallback to timed roll
            try {
              a.removeEventListener("ended", onEnded);
            } catch {}
            endedHandlerRef.current = null;

            endTimeoutRef.current = setTimeout(() => stopRolling([rand1to6(), rand1to6()]), 1500);
          });
        }
      } catch {
        // fallback: no sound
        endTimeoutRef.current = setTimeout(() => stopRolling([rand1to6(), rand1to6()]), 1500);
      }
    } else {
      // No sound: fixed 1.5s roll
      endTimeoutRef.current = setTimeout(() => stopRolling([rand1to6(), rand1to6()]), 1500);
    }
  };

  const reroll = () => {
    if (rolling) return;
    startRolling();
  };

  const effectiveDiceCount = requiresTwoDice ? 2 : diceCount;
  const diceShown = useMemo(
    () => (effectiveDiceCount === 2 ? [result[0], result[1]] : [result[0]]),
    [effectiveDiceCount, result]
  );
  const rollSummary = useMemo(() => {
    const a = Math.max(1, Math.min(6, Number(result[0]) || 1));
    const b = Math.max(1, Math.min(6, Number(result[1]) || 1));
    return effectiveDiceCount === 2 ? `Rolled: ${a} and ${b}` : `Rolled: ${a}`;
  }, [result, effectiveDiceCount]);

  const currentAspectLabel = useMemo(() => {
    const found = ASPECTS.find((x) => x.key === aspect);
    return found?.label ?? "I’ll decide myself";
  }, [aspect]);

  return (
    <NodeViewWrapper
      as="div"
      id={attrs.noteBlockId ? `tool-${attrs.noteBlockId}` : undefined}
      contentEditable={false}
      role="group"
      tabIndex={-1}
      className="relative group w-full rounded-2xl bg-slate-50 shadow-sm p-4 border border-slate-200"
      onMouseDownCapture={(e) => {
        if (!isFormControl(e.target)) e.preventDefault();
        e.stopPropagation();
      }}
      onTouchStartCapture={(e) => {
        if (!isFormControl(e.target)) e.preventDefault();
        e.stopPropagation();
      }}
    >
      <style>{`
        @keyframes diceWiggle {
          0%   { transform: translateY(0) rotate(0deg) scale(1); }
          15%  { transform: translateY(-8px) rotate(-8deg) scale(1.03); }
          35%  { transform: translateY(4px) rotate(10deg) scale(1.02); }
          55%  { transform: translateY(-6px) rotate(-12deg) scale(1.04); }
          75%  { transform: translateY(3px) rotate(8deg) scale(1.02); }
          100% { transform: translateY(0) rotate(0deg) scale(1); }
        }
        .dice.is-rolling {
          animation: diceWiggle 260ms ease-in-out infinite;
        }
      `}</style>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4 min-w-0">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiShuffle} className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="text-lg font-bold text-slate-800">Randomise my practice</div>
            <div className="text-sm text-slate-500">Roll 1 or 2 dice to pick what to do next.</div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 flex-wrap sm:flex-nowrap min-w-0">
          {/* Dice count toggle */}
          <div className="inline-flex rounded-xl border border-slate-200 bg-white/70 p-1">
            <button
              type="button"
              className={cls(
                "px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2",
                effectiveDiceCount === 1 ? "bg-orange-500 text-white shadow" : "text-slate-600 hover:bg-slate-100",
                requiresTwoDice && "opacity-50 cursor-not-allowed"
              )}
              disabled={rolling || requiresTwoDice}
              onClick={() => setDiceCount(1)}
              title={requiresTwoDice ? "This option requires 2 dice" : "Use one die"}
            >
              1 die
            </button>
            <button
              type="button"
              className={cls(
                "px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2",
                effectiveDiceCount === 2 ? "bg-orange-500 text-white shadow" : "text-slate-600 hover:bg-slate-100"
              )}
              disabled={rolling}
              onClick={() => setDiceCount(2)}
              title="Use two dice"
            >
              2 dice
            </button>
          </div>

          {canDelete && (
            <button
              type="button"
              className="p-2 rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 transition-colors opacity-50 group-hover:opacity-100"
              onClick={() => setConfirmOpen(true)}
              title="Delete Tool"
            >
              <SafeIcon icon={FiTrash2} className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="bg-white/80 rounded-2xl border border-slate-200 p-4">
        <div className="flex flex-col items-center justify-center gap-4">
          {/* Aspect selector */}
          <div className="w-full max-w-2xl">
            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">
              What aspect do you want to randomise?
            </label>
            <div className="flex flex-wrap gap-2">
              {ASPECTS.map((o) => {
                const active = o.key === aspect;
                const disabled = rolling; // keep it simple while rolling
                return (
                  <button
                    key={o.key}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      setAspect(o.key);
                      setMessage("");
                    }}
                    className={cls(
                      "px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors",
                      active
                        ? "bg-orange-500 text-white border-orange-500 shadow-sm"
                        : "bg-white/80 text-slate-700 border-slate-300 hover:bg-slate-100",
                      disabled && "opacity-60 cursor-not-allowed"
                    )}
                    title={o.label}
                    aria-pressed={active}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            {requiresTwoDice && (
              <div className="mt-1 text-xs text-slate-500">
                This option uses <span className="font-semibold">two dice</span>.
              </div>
            )}
          </div>

          <div className={cls("flex items-center justify-center gap-4", rolling && "select-none")}>
            {diceShown.map((v, idx) => (
              <DiceFace key={idx} value={v} rolling={rolling} index={idx} />
            ))}
          </div>

          {!rolling && (
            <div className="w-full max-w-2xl">
              <div className="text-xs text-slate-500 text-center">
                <span className="font-semibold text-slate-600">{currentAspectLabel}</span>
                <span className="mx-2 text-slate-300">•</span>
                <span>{rollSummary}</span>
              </div>
              {message && (
                <div className="mt-2 text-sm sm:text-base font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-xl px-4 py-2 text-center">
                  {message}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              className={cls(
                "px-6 py-2.5 rounded-xl text-base font-semibold text-white shadow-lg transition-all transform flex items-center justify-center gap-2",
                rolling ? "bg-slate-400 cursor-not-allowed" : "bg-orange-500 hover:bg-orange-600 active:scale-[0.98]"
              )}
              onClick={startRolling}
              disabled={rolling}
              title="Roll the dice"
            >
              <SafeIcon icon={FiPlay} />
              {rolling ? "Rolling…" : "Roll the dice"}
            </button>

            <button
              type="button"
              className={cls(
                "px-4 py-2.5 rounded-xl text-base font-semibold border bg-white/80 border-slate-300 transition-colors flex items-center justify-center gap-2",
                rolling ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"
              )}
              onClick={reroll}
              disabled={rolling}
              title="Roll again"
            >
              <SafeIcon icon={FiRefreshCcw} />
              Again
            </button>
          </div>

          <div className="text-xs text-slate-500">
            {soundOkRef.current ? "Sound on (roll lasts as long as the sound)." : "Sound unavailable (1.5s roll)."}
          </div>
        </div>
      </div>

      {/* Delete confirm */}
      {confirmOpen && (
        <ConfirmModal
          title="Delete tool?"
          message="Remove this tool from the note? This cannot be undone."
          confirmText="Delete"
          confirmButtonClass="bg-red-500 hover:bg-red-600"
          onConfirm={() => {
            setConfirmOpen(false);
            deleteNode?.();
          }}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </NodeViewWrapper>
  );
}

const RandomisePracticeDiceNode = Node.create({
  name: "randomisePracticeDice",
  group: "block",
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      noteBlockId: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: "randomise-practice-dice" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["randomise-practice-dice", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(RandomisePracticeDiceBlock);
  },
});

export default RandomisePracticeDiceNode;